import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FjSpinner } from 'components/primitives';

const Container = styled.div<{ active: boolean }>`
  position: absolute;
  inset: 0;
  background-color: rgba(35, 31, 32, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: opacity 0.5s;
  pointer-events: none;

  [class^='fj-spinner__Bond'] {
    background-color: white;
  }
`;

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 220px;
`;

const Loader = styled.div<{ percent: number }>`
  height: 7px;
  width: 180px;
  border-radius: 10px;
  margin-top: 25px;
  background-color: white;
  position: relative;
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: ${({ percent }) => percent}%;
    background-color: #8eaeb2;
    transition: width 0.3s;
  }
`;

interface LoadingScreenProps extends React.HTMLAttributes<HTMLDivElement> {
  percent: number;
  loading: boolean;
}

const LoadingScreen = ({ percent, loading, ...props }: LoadingScreenProps) => {
  const showLoadingBar = percent > 0 && percent < 100;
  const shouldBeActive = showLoadingBar || loading;
  const containerRef = React.createRef<HTMLDivElement>();
  const [active, setActive] = useState(shouldBeActive);

  useEffect(() => {
    if (shouldBeActive && !active) {
      setActive(true);
    }
  }, [shouldBeActive, active]);

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.target === containerRef.current && !shouldBeActive) {
      setActive(false);
    }
  };
  if (!active) return null;
  return (
    <Container
      {...props}
      ref={containerRef}
      active={shouldBeActive}
      onTransitionEnd={handleTransitionEnd}
      data-testid="table-loading-overlay"
    >
      <LoadingContent>
        <FjSpinner />
        {showLoadingBar && <Loader percent={percent} />}
      </LoadingContent>
    </Container>
  );
};

export default LoadingScreen;
